import { type FC, useContext } from 'react';
import { useFragment, graphql } from 'react-relay';
import serverVars from 'server-vars';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { WithWalletConnectionEnsured } from 'dibs-crypto-utils/exports/WithWalletConnectionEnsured';
import { ZipCodeFormContent } from './views/ZipCodeFormContent';
import { PlaceBidContent } from './views/PlaceBidContent';
import { BidPendingContent } from './views/BidPendingContent';
import { BidSuccessfulContent } from './views/BidSuccessfulContent';
import { LoadingContent } from './views/LoadingContent';
import { NftPdpBidModalContext } from './NftPdpBidModalContext';
import { BID, BID_SUCCESSFUL, BID_PENDING, ZIP_CODE, LOADING } from './bidModalTypes';
import { type NftPdpBidModalContent_nftItem$key } from './__generated__/NftPdpBidModalContent_nftItem.graphql';
import { type NftPdpBidModalContent_viewer$key } from './__generated__/NftPdpBidModalContent_viewer.graphql';

type Props = {
    nftItem: NftPdpBidModalContent_nftItem$key;
    viewer: NftPdpBidModalContent_viewer$key;
};

export const NftPdpBidModalContent: FC<Props> = ({ nftItem: nftItemRef, viewer: viewerRef }) => {
    const { modalType, onAuthComplete, metamaskConnectTrackingLabel, setModalTypeTrackingLabel } =
        useContext(NftPdpBidModalContext);
    const isMobile = serverVars.get('settings.isMobile');
    const nftItem = useFragment(
        graphql`
            fragment NftPdpBidModalContent_nftItem on NftItem {
                ...PlaceBidContent_nftItem
                ...BidPendingContent_nftItem
                ...BidSuccessfulContent_nftItem
            }
        `,
        nftItemRef
    );
    const viewer = useFragment(
        graphql`
            fragment NftPdpBidModalContent_viewer on Viewer {
                ...WithWalletConnectionEnsured_viewer
                ...ZipCodeFormContent_viewer
            }
        `,
        viewerRef
    );
    return (
        <WithWalletConnectionEnsured
            viewer={viewer}
            isMobile={isMobile}
            onAuthComplete={onAuthComplete}
            setContentTypeLabel={setModalTypeTrackingLabel}
            authFlow={authModalLoader.constants.NFT_BID_FLOW}
            trackingLabel="bid click - NFT PDP"
            metamaskConnectTrackingLabel={metamaskConnectTrackingLabel}
        >
            <>
                {modalType === BID && <PlaceBidContent nftItem={nftItem} />}
                {modalType === BID_PENDING && <BidPendingContent nftItem={nftItem} />}
                {modalType === BID_SUCCESSFUL && <BidSuccessfulContent nftItem={nftItem} />}
                {modalType === ZIP_CODE && <ZipCodeFormContent viewer={viewer} />}
                {modalType === LOADING && <LoadingContent />}
            </>
        </WithWalletConnectionEnsured>
    );
};
